import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RevaText from "components/common/RevaText";
import AvailabilityWindowsSection from "./CompetitionSchedulingParts/AvailabilityWindowsSection";
import WindowDialog from "./CompetitionSchedulingParts/WindowDialog";
import SchedulingPreparationSection from "./CompetitionSchedulingParts/SchedulingPreparationSection";
import EditMatchModal from "../Tournament/EditMatchModal";
import { canApplyDraft } from "./CompetitionSchedulingParts/schedulingDraft";
import SchedulingStepNavigation from "./CompetitionSchedulingParts/SchedulingStepNavigation";
import SchedulingWizardFooter from "./CompetitionSchedulingParts/SchedulingWizardFooter";
import SchedulingRulesStep from "./CompetitionSchedulingParts/SchedulingRulesStep";
import SchedulingReviewStep from "./CompetitionSchedulingParts/SchedulingReviewStep";
import useCompetitionScheduling from "./useCompetitionScheduling";

function CompetitionScheduling({ competitionUuid: competitionUuidProp } = {}) {
  const [activeStep, setActiveStep] = useState(0);
  const [acknowledgeRestEquity, setAcknowledgeRestEquity] = useState(false);
  const {
    id,
    intl,
    loading,
    preparation,
    refreshingPreparation,
    updatingPreparation,
    selectedFinalId,
    setSelectedFinalId,
    saving,
    config,
    schedulingParticipants,
    configSaved,
    configDirty,
    windows,
    availabilityEstablishments,
    fields,
    tournaments,
    windowModalOpen,
    setWindowModalOpen,
    windowForm,
    previewResult,
    incidentPins,
    incidentError,
    anchorConflicts,
    proposalConflicts,
    scheduling,
    confirmRunOpen,
    setConfirmRunOpen,
    confirmClearOpen,
    setConfirmClearOpen,
    canSchedule,
    windowsByDate,
    fieldsForEstablishment,
    windowTimeRangeInvalid,
    refreshPreparation,
    updatePreparation,
    handleConfigChange,
    handleNullableConfigChange,
    handleToggleChange,
    handleDailyRulesChange,
    saveConfig,
    discardConfigChanges,
    editWindow,
    openWindowModal,
    handleWindowFormChange,
    handleEndTimeChange,
    handleEndsAtMidnightChange,
    addDateToForm,
    removeDateFromForm,
    saveWindow,
    deleteWindow,
    runPreview,
    runScheduling,
    clearScheduling,
    handleIncidentChange,
    handleFinalUpdated,
  } = useCompetitionScheduling(competitionUuidProp);

  const steps = [
    {
      label: intl.formatMessage({ id: "scheduling.steps.categories" }),
      description: intl.formatMessage({ id: "scheduling.steps.categoriesHelp" }),
      backLabel: intl.formatMessage({ id: "scheduling.steps.backCategories" }),
    },
    {
      label: intl.formatMessage({ id: "scheduling.steps.rules" }),
      description: intl.formatMessage({ id: "scheduling.steps.rulesHelp" }),
      continueLabel: intl.formatMessage({ id: "scheduling.steps.continueRules" }),
      backLabel: intl.formatMessage({ id: "scheduling.steps.backRules" }),
    },
    {
      label: intl.formatMessage({ id: "scheduling.steps.availability" }),
      description: intl.formatMessage({ id: "scheduling.steps.availabilityHelp" }),
      continueLabel: intl.formatMessage({ id: "scheduling.steps.continueAvailability" }),
      backLabel: intl.formatMessage({ id: "scheduling.steps.backAvailability" }),
    },
    {
      label: intl.formatMessage({ id: "scheduling.steps.review" }),
      description: intl.formatMessage({ id: "scheduling.steps.reviewHelp" }),
      continueLabel: intl.formatMessage({ id: "scheduling.steps.continueReview" }),
    },
  ];
  const footerStatusIds = [
    "scheduling.steps.categoriesStatus",
    "scheduling.steps.rulesStatus",
    "scheduling.steps.availabilityStatus",
    "scheduling.steps.reviewStatus",
  ];
  const unassignedCount = previewResult?.unscheduled_count ?? previewResult?.unscheduled?.length ?? 0;
  const partialApplication = unassignedCount > 0;
  const draftCanBeApplied = canApplyDraft(previewResult);
  const reviewFooterStatus = !previewResult?.draft
    ? intl.formatMessage({ id: footerStatusIds[activeStep] })
    : previewResult.draft.status === "applied"
      ? intl.formatMessage({ id: "scheduling.review.footerApplied" })
      : !draftCanBeApplied
        ? intl.formatMessage({ id: "scheduling.review.footerUnavailable" })
        : intl.formatMessage(
        { id: partialApplication ? "scheduling.review.footerPartial" : "scheduling.review.footerComplete" },
        { count: unassignedCount },
      );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <RevaText variant="h4" id="Calendarización automática" />
      <SchedulingStepNavigation activeStep={activeStep} onStepChange={setActiveStep} steps={steps} />

      {activeStep === 0 && (
        <SchedulingPreparationSection
          preparation={preparation}
          eventId={id}
          intl={intl}
          refreshing={refreshingPreparation || scheduling}
          updating={updatingPreparation}
          onEditFinal={setSelectedFinalId}
          onBulkUpdate={updatePreparation}
        />
      )}

      {activeStep === 1 && (
        <SchedulingRulesStep
          config={config}
          configDirty={configDirty}
          configSaved={configSaved}
          saving={saving}
          scheduling={scheduling}
          windows={windows}
          participants={schedulingParticipants}
          tournaments={tournaments}
          fields={fields}
          handleConfigChange={handleConfigChange}
          handleNullableConfigChange={handleNullableConfigChange}
          handleToggleChange={handleToggleChange}
          handleDailyRulesChange={handleDailyRulesChange}
          saveConfig={saveConfig}
          discardConfigChanges={discardConfigChanges}
          intl={intl}
        />
      )}

      {activeStep === 2 && (
        <AvailabilityWindowsSection
          editWindow={editWindow}
          deleteWindow={deleteWindow}
          fields={fields}
          intl={intl}
          openWindowModal={openWindowModal}
          windows={windows}
          windowsByDate={windowsByDate}
        />
      )}

      {activeStep === 3 && (
        <SchedulingReviewStep
          anchorConflicts={anchorConflicts}
          canSchedule={canSchedule}
          configDirty={configDirty}
          configSaved={configSaved}
          fields={fields}
          incidentError={incidentError}
          incidentPins={incidentPins}
          intl={intl}
          onClear={() => setConfirmClearOpen(true)}
          onEditMatch={setSelectedFinalId}
          onIncidentChange={handleIncidentChange}
          onPreview={runPreview}
          previewResult={previewResult}
          proposalConflicts={proposalConflicts}
          scheduling={scheduling}
          updatingPreparation={updatingPreparation}
        />
      )}

      <SchedulingWizardFooter
        activeStep={activeStep}
        onStepChange={setActiveStep}
        steps={steps}
        status={activeStep === 3 ? reviewFooterStatus : intl.formatMessage({ id: footerStatusIds[activeStep] })}
        actions={activeStep === 3 ? (
          <Button
            variant="contained"
            startIcon={scheduling || refreshingPreparation ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            onClick={async () => {
              await refreshPreparation();
              setAcknowledgeRestEquity(false);
              setConfirmRunOpen(true);
            }}
            disabled={scheduling || refreshingPreparation || !canSchedule || !draftCanBeApplied}
          >
            {intl.formatMessage({
              id: partialApplication ? "scheduling.review.applyPartial" : "scheduling.review.applyFull",
            })}
          </Button>
        ) : null}
      />

      <WindowDialog
        addDateToForm={addDateToForm}
        establishments={availabilityEstablishments}
        fieldsForEstablishment={fieldsForEstablishment}
        handleEndTimeChange={handleEndTimeChange}
        handleEndsAtMidnightChange={handleEndsAtMidnightChange}
        handleWindowFormChange={handleWindowFormChange}
        intl={intl}
        open={windowModalOpen}
        removeDateFromForm={removeDateFromForm}
        saveWindow={saveWindow}
        saving={saving}
        setWindowModalOpen={setWindowModalOpen}
        windowForm={windowForm}
        windowTimeRangeInvalid={windowTimeRangeInvalid}
      />

      <Dialog open={selectedFinalId !== null} onClose={() => setSelectedFinalId(null)} maxWidth="md">
        {selectedFinalId !== null && (
          <EditMatchModal matchId={selectedFinalId} canEdit tabName="details" onSubmitScore={handleFinalUpdated} />
        )}
      </Dialog>
      <Dialog open={confirmRunOpen} onClose={() => setConfirmRunOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "Confirmar calendarización" })}</DialogTitle>
        <DialogContent>
          {(!preparation || preparation.issue_count > 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {preparation
                ? intl.formatMessage({ id: "scheduling.preparation.pending" }, { count: preparation.issue_count })
                : intl.formatMessage({ id: "scheduling.preparation.unavailable" })}{" "}
              {intl.formatMessage({ id: "scheduling.preparation.advisory" })}
            </Alert>
          )}
          {preparation?.tournaments
            .filter((row) => row.issues.length > 0)
            .map((row) => (
              <RevaText key={row.tournament_id} variant="body2" sx={{ mb: 1 }}>
                {row.name}:{" "}
                {row.issues.map((issue) => intl.formatMessage({ id: `scheduling.preparation.${issue}` })).join(" ")}
              </RevaText>
            ))}
          <RevaText>
            {intl.formatMessage({ id: "scheduling.draft.confirm" }, { version: previewResult?.draft?.version ?? "—" })}
          </RevaText>
          {partialApplication && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "scheduling.review.partialConsequence" }, { count: unassignedCount })}
            </Alert>
          )}
          {previewResult?.warnings?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "scheduling.warnings.confirm" }, { count: previewResult.warnings.length })}
              <FormControlLabel
                control={<Checkbox checked={acknowledgeRestEquity} onChange={(event) => setAcknowledgeRestEquity(event.target.checked)} />}
                label={intl.formatMessage({ id: "scheduling.warnings.acknowledge" })}
              />
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRunOpen(false)}>{intl.formatMessage({ id: "Cancelar" })}</Button>
          <Button
            variant="contained"
            onClick={() => runScheduling(acknowledgeRestEquity)}
            disabled={scheduling || refreshingPreparation || !canSchedule || !canApplyDraft(previewResult) || (previewResult?.warnings?.length > 0 && !acknowledgeRestEquity)}
          >
            {intl.formatMessage({ id: "scheduling.review.confirmApply" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "scheduling.review.clear" })}</DialogTitle>
        <DialogContent>
          <RevaText id="scheduling.review.clearConfirm" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>{intl.formatMessage({ id: "Cancelar" })}</Button>
          <Button variant="contained" color="error" onClick={clearScheduling} disabled={scheduling}>
            {intl.formatMessage({ id: "scheduling.review.clear" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompetitionScheduling;
